import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Survey {
  id: string
  title: string
  description: string
  type: string
  status: string
  questions: any[]
  responses_count: number
}

export default function SurveysPage() {
  const [surveys, setSurveys] = useState<Survey[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [npsTrend, setNpsTrend] = useState<any[]>([])
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [survRes, statsRes, npsRes] = await Promise.all([
      fetch(API_BASE + '/surveys'),
      fetch(API_BASE + '/surveys/statistics'),
      fetch(API_BASE + '/surveys/nps-trend')
    ])
    if (survRes.ok) setSurveys((await survRes.json()).surveys || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    if (npsRes.ok) setNpsTrend((await npsRes.json()).trend || [])
  }

  const getNpsColor = (score: number) => {
    if (score >= 50) return 'text-green-600'
    if (score >= 0) return 'text-yellow-600'
    return 'text-red-600'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Surveys & Feedback</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Surveys</div>
              <div className="text-2xl font-bold">{statistics.total_surveys}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_surveys}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Responses</div>
              <div className="text-2xl font-bold">{statistics.total_responses}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">NPS Score</div>
              <div className={`text-2xl font-bold ${getNpsColor(statistics.nps_score)}`}>{statistics.nps_score}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Response Rate</div>
              <div className="text-2xl font-bold">{statistics.response_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h2 className="text-xl font-semibold">Surveys</h2>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Survey</button>
              </div>
              <div className="divide-y">
                {surveys.map(survey => (
                  <div key={survey.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedSurvey(survey)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">{survey.title}</div>
                        <div className="text-sm text-gray-500">{survey.description}</div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs ${survey.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {survey.status}
                        </span>
                        <div className="text-sm text-gray-500 mt-1">{survey.responses_count} responses</div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{survey.type}</span>
                      <span className="text-xs text-gray-500">{survey.questions.length} questions</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">NPS Trend</h2>
              <div className="flex items-end gap-4 h-40">
                {npsTrend.map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center flex-1">
                    <div className={`w-full rounded-t ${getNpsColor(item.score)} bg-current opacity-30`} style={{ height: `${Math.max(item.score + 50, 10)}%` }}></div>
                    <div className="mt-2 text-sm text-gray-500">{item.month}</div>
                    <div className={`text-sm font-semibold ${getNpsColor(item.score)}`}>{item.score}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            {selectedSurvey ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedSurvey.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500">Type</div>
                    <div className="font-medium capitalize">{selectedSurvey.type}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Questions</div>
                    <div className="space-y-2 mt-2">
                      {selectedSurvey.questions.map((q, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded">
                          <div className="text-sm font-medium">{idx + 1}. {q.text}</div>
                          <div className="text-xs text-gray-500 mt-1">Type: {q.type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Survey Details</h3>
                <p className="text-gray-500 text-sm">Select a survey to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
